import React, { useState, useCallback, createContext, useContext, useRef } from 'react';

/* ── Icons ─────────────────────────────────────────────────────────────── */
const SuccessIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill="#22c55e" />
    <path d="M8 14.5l4.5 4.5 7.5-9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill="#ef4444" />
    <path d="M9 9l10 10M19 9L9 19" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill="#3b82f6" />
    <rect x="13" y="12" width="2" height="8" rx="1" fill="#fff" />
    <rect x="13" y="8" width="2" height="2" rx="1" fill="#fff" />
  </svg>
);

const WarningIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill="#f59e0b" />
    <rect x="13" y="8" width="2" height="8" rx="1" fill="#fff" />
    <rect x="13" y="18" width="2" height="2" rx="1" fill="#fff" />
  </svg>
);

const ConfirmIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill="#f59e0b" />
    <text x="14" y="19" textAnchor="middle" fontSize="15" fontWeight="bold" fill="#fff">?</text>
  </svg>
);

/* ── Config per type ────────────────────────────────────────────────────── */
const CONFIG = {
  success: { icon: <SuccessIcon />, color: '#22c55e', label: 'Success' },
  error:   { icon: <ErrorIcon />,   color: '#ef4444', label: 'Error' },
  info:    { icon: <InfoIcon />,    color: '#3b82f6', label: 'Info' },
  warning: { icon: <WarningIcon />, color: '#f59e0b', label: 'Warning' },
  confirm: { icon: <ConfirmIcon />, color: '#f59e0b', label: 'Unsaved Changes' },
};

/* ── Single Toast card ──────────────────────────────────────────────────── */
const ToastCard = ({ id, type = 'info', title, message, actions, onClose }) => {
  const cfg = CONFIG[type] || CONFIG.info;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#fff',
      boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
      minWidth: 320,
      maxWidth: 420,
      overflow: 'hidden',
      marginBottom: 12,
    }}>
      {/* Left colour bar — full height, no padding interference */}
      <div style={{
        width: 5,
        flexShrink: 0,
        background: cfg.color,
      }} />

      {/* Content */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        flex: 1,
        padding: '16px 18px 16px 14px',
      }}>
        {/* Icon */}
        <div style={{ flexShrink: 0, marginTop: 2 }}>{cfg.icon}</div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 2 }}>
            {title || cfg.label}
          </div>
          {message && (
            <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.4 }}>
              {message}
            </div>
          )}
          {actions && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {actions.map((a, i) => (
                <button
                  key={i}
                  onClick={() => { a.onClick?.(); onClose(id); }}
                  style={{
                    padding: '6px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    border: a.primary ? 'none' : '1px solid #d1d5db',
                    background: a.primary ? cfg.color : '#fff',
                    color: a.primary ? '#fff' : '#374151',
                    cursor: 'pointer',
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Close button — hidden when this toast requires an explicit choice */}
        {!actions && (
          <button
            onClick={() => onClose(id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              fontSize: 16,
              lineHeight: 1,
              padding: '0 2px',
              alignSelf: 'flex-start',
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Container — renders all active toasts ──────────────────────────────── */
export const ToastContainer = ({ toasts, onClose }) => {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 24,
      right: 24,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      fontFamily: "'Segoe UI', sans-serif",
    }}>
      {toasts.map(t => (
        <ToastCard key={t.id} {...t} onClose={onClose} />
      ))}
    </div>
  );
};

/* ── Context + hook ─────────────────────────────────────────────────────── */
const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((type, message, title, duration = 6000, actions = null) => {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, type, message, title, actions }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  /* Convenience shortcuts */
  toast.success = (msg, title, dur) => toast('success', msg, title, dur);
  toast.error   = (msg, title, dur) => toast('error',   msg, title, dur);
  toast.info    = (msg, title, dur) => toast('info',    msg, title, dur);
  toast.warning = (msg, title, dur) => toast('warning', msg, title, dur);

  /* Yes/No confirmation toast — stays open until the user picks an option */
  toast.confirm = (message, onYes, onNo, title = 'Unsaved Changes') =>
    toast('confirm', message, title, 0, [
      { label: 'No', onClick: onNo },
      { label: 'Yes, Save', onClick: onYes, primary: true },
    ]);

  /* Delete confirmation toast */
  toast.deleteConfirm = (message, onDelete, title = 'Confirm Delete') =>
    toast('confirm', message, title, 0, [
      { label: 'Cancel' },
      { label: 'Delete', onClick: onDelete, primary: true },
    ]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onClose={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
